/**
 * textFormatting.ts — Shared text formatting utilities for streaming chat.
 *
 * Single source of truth for typing-speed constants and paragraph-break
 * formatting used by both web (TypingText) and native (NativeTypingText).
 */

// ── Typing speed ──────────────────────────────────────────────
// Single source of truth for both web TypingText and native NativeTypingText.
// Tune this value to adjust typing feel across all platforms.
export const TYPING_SPEED = 15; // ms per character

// Adaptive speed thresholds — when pending characters exceed these,
// the animation accelerates to catch up to the stream.
export const ADAPTIVE_THRESHOLDS = {
  HUGE_BACKLOG: 100,   // pending chars → speed / 4
  BIG_BACKLOG: 50,     // pending chars → speed / 3
  MEDIUM_BACKLOG: 20,  // pending chars → speed / 2
};

// ── Paragraph breaks ──────────────────────────────────────────
// Applied during streaming and JSON responses to insert visual paragraph breaks
// between sentences. Prevents wall-of-text during long responses.

export function addParagraphBreaks(text: string): string {
  // Step 1: Standard sentence-boundary paragraph breaks
  let result = text
    .replace(/([.!?])(\s+)([A-Z])/g, '$1\n\n$3')  // Sentence ending + capital letter
    .replace(/([.!?])(\s+)(Whether|To|But|And|Or|So|For|The|A|An|If|When|While|Although|However|Therefore|Thus|Hence)/g, '$1\n\n$2$3');  // Common sentence starters

  // Step 2: Emoji-boundary paragraph breaks
  // Uses \p{Extended_Pictographic} (Unicode property for emoji pictographics, requires /u flag).
  // Handles variation selectors (\uFE0F) and ZWJ (\u200D) for compound emoji sequences.

  // Case A: Punctuation + emoji cluster + capital letter
  // e.g. "...off the hook! 😄💪 My approach" → break before emoji cluster
  result = result.replace(
    /([.!?])\s*((?:\p{Extended_Pictographic}[\uFE0F\u200D]*\s*)+)\s*([A-Z])/gu,
    '$1\n\n$2$3'
  );

  // Case B: No punctuation + emoji cluster + capital letter
  // e.g. "...focused on action 🎉🔥 You can expect" → emoji acts as sentence separator
  // Industry standard: emojis used mid-text as visual punctuation get a paragraph break after
  // the cluster, keeping the emoji with the preceding sentence.
  result = result.replace(
    /([a-z!])\s+((?:\p{Extended_Pictographic}[\uFE0F\u200D]*\s*)+)\s*([A-Z])/gu,
    '$1 $2\n\n$3'
  );

  // Step 3: Normalize any 3+ consecutive newlines to exactly 2 (clean paragraph break)
  // This handles cases where LLM already sent \n\n and function added MORE
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
}
